import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import type { AboutUsEmployees, AboutUsJobOfferContent, AboutUsPageContent, AboutUsTeaser } from '../../api/api-types';
import type { Loadable, Loaded } from '../../guards/ngrx-loader.guard';
import { actions } from './about-us.actions';

interface AboutUsState {
    content: Loadable<AboutUsPageContent>;
    teasers: Loadable<AboutUsTeaser[]>;
    jobOfferContent: Loadable<AboutUsJobOfferContent>;
    employees: Loadable<AboutUsEmployees>;
}

const initialState: AboutUsState = {
    content: null,
    teasers: null,
    jobOfferContent: null,
    employees: null,
};

export const state = createFeature({
    name: 'aboutUs',
    reducer: createReducer(
        initialState,
        on(
            actions.loadAboutUsContentSuccess,
            (state, { content }): AboutUsState => ({
                ...state,
                content,
            }),
        ),
        on(
            actions.loadAboutUsContentFailure,
            (state): AboutUsState => ({
                ...state,
                content: false,
            }),
        ),
        on(
            actions.loadAboutUsTeaserSuccess,
            (state, { teasers }): AboutUsState => ({
                ...state,
                teasers,
            }),
        ),
        on(
            actions.loadAboutUsTeaserFailure,
            (state): AboutUsState => ({
                ...state,
                teasers: false,
            }),
        ),
        on(
            actions.loadJobOfferContentSuccess,
            (state, { jobOfferContent }): AboutUsState => ({
                ...state,
                jobOfferContent,
            }),
        ),
        on(
            actions.loadEmployeesSuccess,
            (state, { employees }): AboutUsState => ({
                ...state,
                employees,
            }),
        ),
        on(
            actions.loadEmployeesFailure,
            (state): AboutUsState => ({
                ...state,
                employees: false,
            }),
        ),
    ),
    extraSelectors: (baseSelectors) => {
        const selectIsLoadingAboutUsContent = createSelector(
            baseSelectors.selectContent,
            (content) => content === null,
        );
        const selectLoadedAboutUsContent = createSelector(
            baseSelectors.selectContent,
            (content) => content as Loaded<AboutUsPageContent>,
        );
        const selectLoadedAboutUsEmployees = createSelector(
            baseSelectors.selectEmployees,
            (employees) => employees as Loaded<AboutUsEmployees>,
        );
        return {
            selectIsLoadingAboutUsContent,
            selectLoadedAboutUsContent,
            selectLoadedAboutUsEmployees,
        };
    },
});
